import { makeAutoObservable, reaction, runInAction } from "mobx";

import { AccessType } from "@/types/enums/AccessType";
import { LoggedOnUserInfoDTO, User, UserCompany, GetUserSettingsResponse } from "@/types/UserInfo";
import { CompanyInfo } from "@/types/CompanyInfo";
import { ShareholderViewResponse } from "@/types/Shareholder";
import { get, omit, sortBy } from "lodash";
import {
  CompanyItem,
  failure,
  isFailure,
  ok,
  pending,
  Portfolio,
  Result,
  SignDocumentCount,
  SigningMethod,
} from "@/common/common";
import { AxiosError } from "axios";
import { ApiService } from "@/api";
import { History } from "history";
import { Language } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import {
  navigateBackWithFallback,
  navigateToBatchSigningPage,
  navigateToShareholderDetail,
} from "@/utils/navigation";
import {
  EventDispatcher,
  showCreatePortfolioDialog,
  showPaymentDialog,
} from "@/events/eventsDomain";
import { PaidSubscription, SubscriptionStatus, SubscriptionType } from "@/common/subscription";
import {
  allowAll,
  allowNone,
  CustomRole,
  CustomRoleForUser,
  mergeRoles,
} from "@/common/customRole";
import { isBlank } from "@/utils/strings";
import { PortfolioCompany } from "./PortfolioStore";
import { InvitationType } from "@/types/Invitation";
import { ShareholderDetailsTab } from "@/shareholderRegister/shareholders/shareholderDetailsTypes";
import { CURRENCY_NOK } from "@/utils/currency";
import { FeatureToggles, PortfolioFeatureToggles } from "@/common/featureToggles";
import { isRegisterSubscription } from "@/payment/ApplicationFeatures";
import { EnterpriseAccountItem, filterCompaniesWithDbotAccess } from "@/portfolio/portfolioDomain";
import { updatePortfolioSearchState } from "@/portfolio/search/portfolioSearchDomain";

export type MfaChallenge = {
  type: "mfa";
  method?: string;
  recipient?: string;
};

export type LoginStatus = "none" | "logged-in" | "timed-out" | "required" | MfaChallenge;

export type MfaChallengeResponse = {
  error: "mfa";
  method?: string;
  recipient?: string;
};

export function isMfaChallengeResponse(body: unknown): body is MfaChallengeResponse {
  return get(body, "error") === "mfa";
}

export function mapToLoginStatus(errorPayload: unknown, currentStatus: LoginStatus): LoginStatus {
  if (isMfaChallengeResponse(errorPayload)) {
    return { type: "mfa", ...omit(errorPayload, "error") };
  } else if (currentStatus === "logged-in") {
    return "timed-out";
  } else {
    return "required";
  }
}

export function isLoginRequired(status: LoginStatus): boolean {
  return status !== "none" && status !== "logged-in";
}

export function isMfaChallenge(status: LoginStatus): status is MfaChallenge {
  return get(status, "type") === "mfa";
}

const PREVIOUS_LOCATION_KEY = "previous-location";
const ROLE_PREVIEW_KEY = "role-preview";

type RolePreview = { orgNo: string; roleId: string };

const readRolePreview = (): RolePreview | undefined => {
  const stored = sessionStorage.getItem(ROLE_PREVIEW_KEY);
  return stored ? JSON.parse(stored) : undefined;
};

export default class UserStore {
  orgNumber = "";
  accessType: AccessType | null = null;
  shareholderId?: number;
  user: User | null = null;
  userSettings: GetUserSettingsResponse | null = null;
  companies: PortfolioCompany[] = [];
  portfolios: Portfolio[] = [];
  subscriptionStatusByOrgNumber: Map<string, PaidSubscription> = new Map();
  signDocumentCounts: SignDocumentCount[] = [];
  userRoles: CustomRoleForUser[] = [];
  requireSsoReauthentication = false;
  requiredSsoProviders: string[] = [];
  requiredSsoAccountNames: string[] = [];
  portfolioFeatureToggles: PortfolioFeatureToggles = {};
  enterpriseAccountItems: EnterpriseAccountItem[] = [];
  userError?: TMessage;
  companyInfo: CompanyInfo | null = null;
  shareholderViewResult: Result<ShareholderViewResponse> | undefined;
  isUserLoading = false;
  isCompanyLoading = false;
  loginStatus: LoginStatus = "none";
  authenticationUrlParameters = "";

  dispatchEvent: EventDispatcher;
  apiService: ApiService;
  history: History;

  customRoleId?: string;
  rolePreview: RolePreview | undefined = readRolePreview();

  get userRole(): CustomRoleForUser {
    if (isBlank(this.orgNumber)) {
      return allowNone;
    } else if (this.accessType === AccessType.ADMIN) {
      return allowAll;
    } else if (this.accessType === AccessType.CUSTOM) {
      const customRoleId = this.previewRoleIdFor(this.orgNumber) ?? this.customRoleId;
      if (customRoleId) {
        const role = this.userRoles.find((r) => r.customRoleId === customRoleId);
        if (role) {
          return role;
        }
      }
      return mergeRoles(this.userRoles, this.orgNumber);
    } else {
      return allowNone;
    }
  }

  setCustomRoleId(roleId: string) {
    this.customRoleId = roleId;
    this.accessType = AccessType.CUSTOM;
  }

  get isPreviewingRole(): boolean {
    return (
      this.accessType === AccessType.CUSTOM && this.previewRoleIdFor(this.orgNumber) !== undefined
    );
  }

  previewRoleIdFor(orgNo: string): string | undefined {
    return this.rolePreview?.orgNo === orgNo ? this.rolePreview.roleId : undefined;
  }

  startRolePreview(rolePreview: RolePreview) {
    this.rolePreview = rolePreview;
    sessionStorage.setItem(ROLE_PREVIEW_KEY, JSON.stringify(rolePreview));
  }

  setPreviousLocation(location: string) {
    sessionStorage.setItem(PREVIOUS_LOCATION_KEY, location);
  }

  clearPreviousLocation() {
    sessionStorage.removeItem(PREVIOUS_LOCATION_KEY);
  }

  getPreviousLocation() {
    return sessionStorage.getItem(PREVIOUS_LOCATION_KEY);
  }

  get isAuthenticated() {
    return this.user !== null && this.user.userId > 0;
  }

  get isLoading() {
    return this.isUserLoading || this.isCompanyLoading;
  }

  get isShareholderView() {
    return this.accessType === AccessType.SHAREHOLDER;
  }

  get isShareholderOrRepresentativeView() {
    return (
      this.accessType === AccessType.SHAREHOLDER ||
      this.accessType === AccessType.ADDITIONAL_REPRESENTATIVE
    );
  }

  get availablePortfolioFeatures(): PortfolioFeatureToggles {
    return this.hasOnlyShareholderAccess ? {} : this.portfolioFeatureToggles;
  }

  get hasOnlyShareholderAccess() {
    return this.companies.every(
      (c) =>
        c.accessType === AccessType.SHAREHOLDER ||
        c.accessType === AccessType.ADDITIONAL_REPRESENTATIVE,
    );
  }

  get isLoginRequired() {
    return isLoginRequired(this.loginStatus);
  }

  private setLoginStatus(status: LoginStatus) {
    this.loginStatus = status;
  }

  clearMfaChallenge() {
    if (isMfaChallenge(this.loginStatus)) {
      this.loginStatus = "required";
    }
  }

  get isLoadError() {
    return this.userError && !this.isLoginRequired;
  }

  constructor(apiService: ApiService, history: History, dispatchEvent: EventDispatcher) {
    this.apiService = apiService;
    this.history = history;
    this.dispatchEvent = dispatchEvent;
    makeAutoObservable(this, {}, { autoBind: true });

    reaction(
      () => this.user?.userId && this.orgNumber,
      (orgNumber, prevOrgNumber) => {
        if (orgNumber && orgNumber !== prevOrgNumber) {
          if (
            this.companiesWithAccess.some(
              (a) => a.organisationNumber === orgNumber && a.accessType === this.accessType,
            )
          ) {
            this.updateCompanyInfo();
          } else {
            this.orgNumber = "";
            this.companyInfo = null;
            this.shareholderId = undefined;
            this.setAccessType(null);
          }
        }
      },
    );
  }

  init() {
    this.fetchLoggedOnUserInformation().finally(() => {
      setInterval(() => {
        this.checkAuthStatus();
      }, 60000);
    });
  }

  removePortfolio(portfolioId: string) {
    this.portfolios = this.portfolios.filter((p) => p.id !== portfolioId);
    this.dispatchEvent([{ type: "update-store", data: { followUpCounts: undefined } }]);
    this.refreshUserInformation();
  }

  updatePortfolio(portfolio: Portfolio) {
    const index = this.portfolios.findIndex((p) => p.id === portfolio.id);
    this.portfolios[index] = portfolio;
  }

  get companiesWithAccess(): PortfolioCompany[] {
    return sortBy(
      this.companies.filter((c) => c.accessType !== AccessType.NONE),
      "name",
    );
  }

  get hasSuperadminAccess(): boolean {
    return this.companiesWithAccess.some((c) => c.accessType === AccessType.SUPERADMIN);
  }

  get companiesWithAdminAccess(): UserCompany[] {
    return this.companiesWithAccess.filter((c) => c.accessType === AccessType.ADMIN);
  }

  get companiesWithDbotAccess(): UserCompany[] {
    return filterCompaniesWithDbotAccess(
      this.companiesWithAccess,
      Array.from(this.subscriptionStatusByOrgNumber.values()),
    );
  }

  getCompanyInfo: () => CompanyInfo | null = () => {
    return this.companyInfo;
  };

  *checkAuthStatus() {
    if (this.isLoginRequired) {
      // skipping auth check while on the login page
      return;
    }
    try {
      yield this.apiService.authStatus();
    } catch (error) {
      // handled by request interceptor
    }
  }

  private fetchLoggedOnUserInformation = async () => {
    try {
      this.setUserLoading(true);
      this.setUserInfo(await this.apiService.fetchUserInfo());
      this.setLoginStatus("logged-in");
    } catch (error) {
      this.setUserError({ key: "user.failedToFetchUserInfo" });
    } finally {
      this.setUserLoading(false);
    }
  };

  *refreshUserInformation() {
    this.isUserLoading = true;
    try {
      const userInfo: LoggedOnUserInfoDTO = yield this.apiService.fetchUserInfo();
      this.setUserInfo(userInfo);
    } catch (e) {
      this.userError = { key: "user.failedToFetchUserInfo" };
    } finally {
      this.isUserLoading = false;
    }
  }

  setUserInfo(userInfo: LoggedOnUserInfoDTO) {
    this.user = userInfo.user;
    this.userSettings = userInfo.userSettings;
    this.companies = userInfo.companies;
    this.portfolios = userInfo.portfolios;
    this.subscriptionStatusByOrgNumber = new Map(
      userInfo.subscriptionStatuses.map((s) => [s.organisationNumber, s]),
    );
    this.signDocumentCounts = userInfo.signDocumentCounts;
    this.userRoles = userInfo.userRoles;
    this.requireSsoReauthentication = userInfo.requireSsoReauthentication ?? false;
    this.requiredSsoProviders = userInfo.requiredSsoProviders ?? [];
    this.requiredSsoAccountNames = userInfo.requiredSsoAccountNames ?? [];
    this.portfolioFeatureToggles = userInfo.portfolioFeatureToggles ?? {};
    this.enterpriseAccountItems = userInfo.enterpriseAccountItems ?? [];
    if (userInfo.portfolioOverview) {
      this.dispatchEvent([updatePortfolioSearchState({ overview: userInfo.portfolioOverview })]);
    }
  }

  clearUserInfo() {
    this.user = null;
    this.userSettings = null;
    this.companies = [];
  }

  setEmail(email: string) {
    if (this.user) {
      this.user.email = email;
    }
  }

  setUserTermsAccepted(email: string) {
    if (this.user) {
      this.user.email = email;
      this.user.acceptedTerms = true;
    }
    this.refreshUserInformation();
  }

  openPaymentDialog = () => {
    this.dispatchEvent([showPaymentDialog]);
  };

  openCreatePortfolioDialog() {
    this.dispatchEvent([showCreatePortfolioDialog]);
  }

  fetchShareholderInfo = async (
    orgNumber: string,
    shareholderId: number,
    options?: { updateLastVisited?: boolean },
  ) => {
    this.shareholderViewResult = pending();
    try {
      const shareholderInfo = await this.apiService.fetchShareholderView(
        orgNumber,
        shareholderId,
        options,
      );
      runInAction(() => (this.shareholderViewResult = ok(shareholderInfo)));
    } catch (error) {
      runInAction(() => {
        this.shareholderViewResult = failure();
      });
    }
  };

  updateCompanyInfo = async () => {
    try {
      this.setCompanyLoading(true);
      if (this.isShareholderOrRepresentativeView && this.shareholderId) {
        await this.fetchShareholderInfo(this.orgNumber, this.shareholderId, {
          updateLastVisited: true,
        });
      } else {
        const companyInfo = await this.apiService.fetchCompanyInfo(this.orgNumber, {
          updateLastVisited: true,
        });
        this.setCompanyInfo(companyInfo);
        if (this.isAdmin && companyInfo.acceptedTerms && companyInfo.forceReacceptTerms) {
          const userInfo = await this.apiService.fetchUserInfo();
          this.dispatchEvent([
            {
              type: "showAcceptCompanyTermsDialog",
              companyInfo: companyInfo,
              user: userInfo.user,
            },
          ]);
        }
      }
    } catch (error) {
      runInAction(() => {
        this.orgNumber = "";
      });
      this.setUserError({ key: "user.failedToUpdateCompanyInfo" });
    } finally {
      this.setCompanyLoading(false);
      if (isFailure(this.shareholderViewResult)) {
        this.setUserError({ key: "error.fetch.shareholderinfo" });
      }
    }
  };

  logout = () => {
    sessionStorage.clear();
    this.apiService.logout().then(() => {
      window.location.href = "/login";
    });
  };

  setAccessType = (accessType: AccessType | string | null) => {
    if (Object.values(AccessType).includes(accessType as AccessType)) {
      this.accessType = accessType as AccessType;
    } else {
      this.accessType = null;
    }
  };

  get isAdmin(): boolean {
    return this.accessType === AccessType.ADMIN;
  }

  get companyName(): string | null {
    return (
      this.companiesWithAccess.find(
        (access) =>
          access.organisationNumber === this.orgNumber && access.accessType === this.accessType,
      )?.name || null
    );
  }

  get currency(): string {
    return this.companyInfo?.company?.currency ?? CURRENCY_NOK;
  }

  get shareNominalValue(): number {
    return this.companyInfo?.shareNominalValue ?? 0;
  }

  hasAccess({
    organisationNumber,
    accessType,
    shareholderId,
  }: {
    organisationNumber: string;
    accessType: AccessType;
    shareholderId?: number;
  }): boolean {
    return this.companiesWithAccess.some(
      (c) =>
        c.organisationNumber === organisationNumber &&
        c.accessType === accessType &&
        (shareholderId === undefined || c.shareholderId === shareholderId),
    );
  }

  setSelectedCompany({
    organisationNumber,
    accessType,
    shareholderId,
  }: {
    organisationNumber: string;
    accessType: AccessType;
    shareholderId?: number;
  }) {
    this.orgNumber = organisationNumber;
    this.setAccessType(accessType);
    this.shareholderId = shareholderId;
  }

  setCompanyInfo(companyInfo: CompanyInfo | null) {
    this.companyInfo = companyInfo;
    if (companyInfo) {
      this.syncCompanyName(companyInfo.company.organizationnumber, companyInfo.company.name);
    }
  }

  syncCompanyName(organisationNumber: string, name: string) {
    const isOutdated = (c: UserCompany) =>
      c.organisationNumber === organisationNumber && c.name !== name;
    if (!this.companies.some(isOutdated)) {
      return;
    }
    this.companies = this.companies.map((c) => (isOutdated(c) ? { ...c, name } : c));
  }

  setAuthenticationUrlParameters(
    invitationId: string,
    invitationToken: string,
    idParameterName = "invitationId",
  ) {
    this.authenticationUrlParameters = `?${idParameterName}=${invitationId}&invitationToken=${invitationToken}`;
  }

  clearCompany() {
    this.orgNumber = "";
    this.setAccessType(null);
    this.setCompanyInfo(null);
  }

  private setUserError(userError: TMessage) {
    this.userError = userError;
  }

  private setUserLoading(isLoading: boolean) {
    this.isUserLoading = isLoading;
  }

  private setCompanyLoading(isLoading: boolean) {
    this.isCompanyLoading = isLoading;
  }

  showInviteUserDialog(companyItem: CompanyItem, lang: Language, role?: CustomRole) {
    this.dispatchEvent([
      {
        type: "showSendInvitationDialog",
        data: {
          invitationType: InvitationType.USER,
          organisationNumber: companyItem.organisationNumber,
          companyName: companyItem.companyName,
          lang,
          title:
            role !== undefined
              ? { key: "roles.invite-new", params: { roleName: role.customRoleName } }
              : { key: "invite-user.title" },
          description: {
            key: "invite-user.infoMessage",
            params: { companyName: companyItem.companyName },
          },
        },
      },
    ]);
  }

  get paidSubscription(): PaidSubscription | undefined {
    return this.subscriptionStatusByOrgNumber.get(this.orgNumber);
  }

  get isShareholderRegisterSubscriptionType() {
    return this.paidSubscription?.subscriptionType.startsWith("SHAREHOLDER_REGISTER");
  }

  get isRegisterSubscriptionType() {
    return isRegisterSubscription(this.paidSubscription);
  }

  get isPartnership(): boolean {
    return this.companyInfo?.ownershipModel === "partnership_interests";
  }

  get hasShareBasedOwnership(): boolean {
    const ownershipModel = this.companyInfo?.ownershipModel;
    return ownershipModel === undefined || ownershipModel === "shares";
  }

  get shouldRenderOnboardingSection() {
    return !this.isShareholderRegisterSubscriptionType && !this.isRegisterSubscriptionType;
  }

  get shouldRenderShortcuts() {
    return (
      this.paidSubscription?.allowsDbot &&
      (this.accessType === AccessType.ADMIN ||
        (this.accessType === AccessType.CUSTOM &&
          mergeRoles(this.userRoles, this.orgNumber).allowDbot))
    );
  }

  get hasAnyActiveSubscription(): boolean {
    return this.hasActiveSubscriptionStatus();
  }

  get hasInactiveShareholderRegisterSubscription(): boolean {
    return (
      this.paidSubscription?.subscriptionStatus === SubscriptionStatus.CREATED_INACTIVE &&
      this.paidSubscription?.subscriptionType === SubscriptionType.SHAREHOLDER_REGISTER_LEGACY
    );
  }

  get hasShareholderRegisterSubscription(): boolean {
    return SubscriptionType.SHAREHOLDER_REGISTER_LEGACY === this.paidSubscription?.subscriptionType;
  }

  get hasActiveShareholderRegisterSubscription(): boolean {
    return (
      this.paidSubscription?.subscriptionStatus === SubscriptionStatus.ACTIVE &&
      SubscriptionType.SHAREHOLDER_REGISTER_LEGACY === this.paidSubscription?.subscriptionType
    );
  }

  private hasActiveSubscriptionStatus() {
    return (
      this.paidSubscription?.subscriptionStatus === SubscriptionStatus.ACTIVE ||
      this.paidSubscription?.subscriptionStatus === SubscriptionStatus.PAYMENT_REGISTERED
    );
  }

  *acceptCompanyTerms() {
    if (!this.companyInfo) {
      return;
    }
    yield this.apiService.acceptCompanyTerms(this.companyInfo.company.organizationnumber);
    if (this.companyInfo) {
      this.companyInfo.acceptedTerms = true;
    }
  }

  *updateUserLanguage(language: Language) {
    if (this.isAuthenticated) {
      if (this.user) {
        this.user.language = language;
      }
      try {
        yield this.apiService.updateUserLanguage(language);
      } catch (e) {
        // no action
      }
    }
  }

  handleAuthError(err: AxiosError) {
    this.setLoginStatus(mapToLoginStatus(err.response?.data, this.loginStatus));
    this.clearUserInfo();
  }

  get isNorwegianAuthenticatedCountry(): boolean {
    return Language.NO.toUpperCase() === this.user?.authenticatedCountry;
  }

  get hasAnyAdminRole(): boolean {
    return this.companies.some((c) => c.accessType === AccessType.ADMIN);
  }

  get hasBoardRole(): boolean {
    return this.companies.some((c) => c.accessType === AccessType.BOARD_MEMBER);
  }

  shouldRenderCompanyAsUnconfirmed() {
    return (
      this.isAdmin &&
      this.companyInfo &&
      (!this.companyInfo.acceptedTerms || !this.companyInfo.shareNominalValueConfirmed)
    );
  }

  navigateToBatchSigningPage(signingMethod: SigningMethod, currentUrl: string) {
    this.setPreviousLocation(currentUrl);
    navigateToBatchSigningPage(this.history, signingMethod);
  }

  closeBatchSigningPage() {
    navigateBackWithFallback(this.history, this.getPreviousLocation());
    this.dispatchEvent([
      { type: "update-store", data: { signDocument: { type: "loading-details", ref: "" } } },
    ]);
    this.refreshUserInformation();
    this.clearPreviousLocation();
  }

  sendEmailVerificationCode(emailAddress: string) {
    this.apiService.sendEmailVerificationCode(emailAddress);
  }

  navigateToShareholderDetail(
    shareholderId: number,
    currentUrl: string,
    queryString?: string,
    tab?: ShareholderDetailsTab,
  ) {
    this.setPreviousLocation(currentUrl);
    navigateToShareholderDetail(
      this.orgNumber,
      shareholderId,
      this.history,
      queryString,
      tab,
      currentUrl,
    );
  }

  get featureToggles(): FeatureToggles {
    return this.companyInfo?.featureToggles || {};
  }
}
